/**
 * Puter.js AI client for PitchLab
 * Provides AI evaluation capabilities through Puter's cloud service
 * 
 * Documentation: https://docs.puter.com/
 * No API key required - Puter SDK handles authentication automatically
 */

/**
 * Initialize Puter AI client
 * Note: Puter.js is loaded via script tag in index.html
 * @returns {Object} Puter AI interface
 */
const initializePuterAI = () => {
  // Check if Puter is available
  if (typeof window !== 'undefined' && typeof window.puter === 'undefined') {
    // Puter SDK not loaded - silently return null (no longer in use)
    return null;
  }

  // Access puter from window object
  const puter = typeof window !== 'undefined' ? window.puter : null;

  return {
    /**
     * Generate AI completion using Puter's AI service
     * @param {string} prompt - The prompt to send to the AI
     * @param {Object} options - Additional options for the AI request
     * @returns {Promise<Object>} AI generated response object
     */
    async generateContent(prompt, options = {}) {
      try {
        // Use Puter's AI chat API with Claude models as shown in documentation
        const response = await puter?.ai?.chat(prompt, {
          model: options?.model || 'claude-haiku-4-5', // Default to fast Claude model
          temperature: options?.temperature || 0.7,
          ...options
        });

        return response;
      } catch (error) {
        console.error('Puter AI generation error:', error);
        throw new Error(`Puter AI request failed: ${error?.message}`);
      }
    },

    /**
     * Evaluate sales drill response with structured output
     * @param {Object} evaluationData - Data for evaluation
     * @returns {Promise<Object>} Structured evaluation result
     */
    async evaluateDrillResponse(evaluationData) {
      const {
        industry,
        drillScenario,
        idealAnswer,
        coachingNotes,
        userResponse,
        personalityPrompt,
        coachingStage
      } = evaluationData;

      // Use personality prompt if provided, otherwise default
      const baseSystemPrompt = personalityPrompt || `You are Lumo, an expert sales coach with deep knowledge in consultative selling, objection handling, and closing techniques.`;

      const systemPrompt = `${baseSystemPrompt}

DRILL CONTEXT:
Industry: ${industry || 'General Sales'}
Scenario: ${drillScenario || 'Sales roleplay scenario'}
Ideal Answer: ${idealAnswer || 'Begin with active listening to understand their specific needs...'}
Coaching Notes: ${coachingNotes || 'Focus on clarity, confidence, control, and moving toward a concrete next step.'}
${coachingStage ? `\nCOACHING STAGE: ${coachingStage}` : ''}

USER'S RESPONSE:
"${userResponse}"

EVALUATION TASK:
Evaluate the user's response using the following 4C Framework (use your coaching personality above):

1. CLARITY (0-100): How clear and articulate is the response? Is it easy to understand?
2. CONFIDENCE (0-100): Does the response demonstrate confidence without being aggressive?
3. CONTROL (0-100): Does the response maintain control of the conversation and guide it forward?
4. CLOSE (0-100): Does the response move toward a clear next step or close?

Provide your evaluation in this EXACT JSON format (no markdown, no code blocks):
{
  "overall_score": <number 0-100>,
  "clarity_score": <number 0-100>,
  "confidence_score": <number 0-100>,
  "control_score": <number 0-100>,
  "close_score": <number 0-100>,
  "feedback_text": "<2-3 paragraph coaching feedback in your personality's tone>",
  "suggested_script": "<A rewritten version of their response that scores 90+ on all metrics>"
}

CRITICAL: Return ONLY the JSON object above. No additional text, explanation, or markdown formatting.`;


      try {
        // Use Claude Opus for more detailed evaluations
        const response = await this.generateContent(systemPrompt, {
          model: 'claude-opus-4-5',
          temperature: 0.7
        });

        // Puter.ai.chat returns response with message.content array
        let evaluationText = response?.message?.content?.[0]?.text || response?.text || '';
        
        // Clean up response
        let cleanedResponse = evaluationText?.trim();
        if (cleanedResponse?.startsWith('```json')) {
          cleanedResponse = cleanedResponse?.slice(7);
        }
        if (cleanedResponse?.startsWith('```')) {
          cleanedResponse = cleanedResponse?.slice(3);
        }
        if (cleanedResponse?.endsWith('```')) {
          cleanedResponse = cleanedResponse?.slice(0, -3);
        }
        cleanedResponse = cleanedResponse?.trim();

        const evaluation = JSON.parse(cleanedResponse);

        return {
          scores: {
            clarity: evaluation?.clarity_score || 0,
            confidence: evaluation?.confidence_score || 0,
            control: evaluation?.control_score || 0,
            close: evaluation?.close_score || 0,
            overall: evaluation?.overall_score || 0
          },
          summary: evaluation?.feedback_text || 'Evaluation completed',
          strengths: [], // Puter AI provides combined feedback
          improvements: [], // Puter AI provides combined feedback
          sampleResponse: evaluation?.suggested_script || ''
        };
      } catch (error) {
        console.error('Error parsing Puter AI evaluation:', error);
        throw error;
      }
    },

    /**
     * Evaluate objection handling response
     * @param {Object} evaluationData - Data for objection evaluation
     * @returns {Promise<Object>} Structured evaluation result
     */
    async evaluateObjectionResponse(evaluationData) {
      const {
        industry,
        objectionScenario,
        idealResponse,
        coachingNotes,
        userResponse,
        personalityPrompt,
        coachingStage
      } = evaluationData;

      // Use personality prompt if provided, otherwise default
      const baseSystemPrompt = personalityPrompt || `You are Lumo, an AI sales coach inside an app called Earn My Edge.`;

      const systemPrompt = `${baseSystemPrompt}

OBJECTION CONTEXT:
Industry: ${industry || 'General Sales'}
Customer Objection: ${objectionScenario || 'Customer objection scenario'}
Ideal Response: ${idealResponse || 'Acknowledge the concern, validate their perspective...'}
Coaching Notes: ${coachingNotes || 'Focus on acknowledging concerns, reframing, and moving forward.'}
${coachingStage ? `\nCOACHING STAGE: ${coachingStage}` : ''}

USER'S RESPONSE:
"${userResponse}"

Evaluate the rep's answer focusing on (use your coaching personality above):
- Does it acknowledge the customer's concern?
- Does it reframe the objection positively?
- Does it provide value and move forward?
- Does it maintain control without being pushy?

Provide your evaluation in this EXACT JSON format (no markdown, no code blocks):
{
  "lumo_score": <number 0-100>,
  "lumo_feedback_text": "<2-3 paragraph coaching feedback in your personality's tone>",
  "lumo_suggested_script": "<A rewritten version that handles the objection effectively>"
}

CRITICAL: Return ONLY the JSON object. No additional text or formatting.`;


      try {
        // Use Claude Opus for detailed objection handling evaluation
        const response = await this.generateContent(systemPrompt, {
          model: 'claude-opus-4-5',
          temperature: 0.7
        });

        // Puter.ai.chat returns response with message.content array
        let evaluationText = response?.message?.content?.[0]?.text || response?.text || '';

        // Clean up response
        let cleanedResponse = evaluationText?.trim();
        if (cleanedResponse?.startsWith('```json')) {
          cleanedResponse = cleanedResponse?.slice(7);
        }
        if (cleanedResponse?.startsWith('```')) {
          cleanedResponse = cleanedResponse?.slice(3);
        }
        if (cleanedResponse?.endsWith('```')) {
          cleanedResponse = cleanedResponse?.slice(0, -3);
        }
        cleanedResponse = cleanedResponse?.trim();

        const evaluation = JSON.parse(cleanedResponse);

        return {
          scores: {
            clarity: evaluation?.lumo_score || 0,
            confidence: evaluation?.lumo_score || 0,
            control: evaluation?.lumo_score || 0,
            close: evaluation?.lumo_score || 0,
            overall: evaluation?.lumo_score || 0
          },
          summary: evaluation?.lumo_feedback_text || 'Evaluation completed',
          strengths: [],
          improvements: [],
          sampleResponse: evaluation?.lumo_suggested_script || ''
        };
      } catch (error) {
        console.error('Error parsing Puter AI objection evaluation:', error);
        throw error;
      }
    }
  };
};

// Export Puter AI interface
export default initializePuterAI();